import { useCallback, useEffect, useState } from "react";
import { toPredictionAccessPopup } from "../features/predictions/accessError";
import { applyPredictionUserUpdate } from "../features/predictions/postSuccessUserUpdate";

function chargedCreditsFromResponse(predictionResponse) {
  const raw = predictionResponse?.prediction?.credit_cost ?? predictionResponse?.prediction?.creditCost;
  const charged = Number(raw);
  return Number.isFinite(charged) ? charged : null;
}

/**
 * Shared analyze → bill → access-popup flow (couple match).
 */
export function usePredictionRun({
  isReplayMode,
  authUser,
  onCreditsUpdate,
  t,
  runAnalyze,
}) {
  const [loading, setLoading] = useState(false);
  const [pendingPredictionResponse, setPendingPredictionResponse] = useState(null);
  const [showAccessPopup, setShowAccessPopup] = useState(false);
  const [accessPopupCopy, setAccessPopupCopy] = useState({ title: "", message: "" });

  useEffect(() => {
    if (isReplayMode || !pendingPredictionResponse || loading) return;
    applyPredictionUserUpdate({ authUser, predictionResponse: pendingPredictionResponse, onCreditsUpdate });
    setPendingPredictionResponse(null);
  }, [isReplayMode, pendingPredictionResponse, loading, authUser, onCreditsUpdate]);

  const execute = useCallback(async () => {
    if (loading || isReplayMode) return { ok: false };
    setLoading(true);
    try {
      const runOutcome = await runAnalyze();
      const predictionResponse = runOutcome?.predictionResponse ?? runOutcome;
      setPendingPredictionResponse(predictionResponse);
      return {
        ok: true,
        predictionResponse,
        chargedCredits: chargedCreditsFromResponse(predictionResponse),
        ...(runOutcome?.computed != null ? { computed: runOutcome.computed } : {}),
      };
    } catch (error) {
      const popupPayload = toPredictionAccessPopup(error, t);
      if (popupPayload.shouldShowPopup) {
        setAccessPopupCopy({ title: popupPayload.title, message: popupPayload.message });
        setShowAccessPopup(true);
        return { ok: false, paymentRequired: true };
      }
      return { ok: false, error };
    } finally {
      setLoading(false);
    }
  }, [loading, isReplayMode, runAnalyze, t]);

  return {
    loading,
    showAccessPopup,
    setShowAccessPopup,
    accessPopupCopy,
    execute,
  };
}
